/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SelectGateway.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Reusable component for selecting a gateway with search functionality.
    Used in outbound routes gateway selection.

 🔧 Notes:
    - ID comparison uses String() coercion to handle Sqid string IDs
      from the /api/gateways endpoint (e.g. 'qdZW4AXl' not integer)
    - onMouseDown used for selection to avoid race condition with
      document-level mousedown outside-click detection

 🔁 Last Updated: 2026-03-13 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface Gateway {
  id: string | number;
  name: string;
  description?: string;
}

interface SelectGatewayProps {
  value: string | number | null;
  onChange: (value: string | number) => void;
  gateways: Gateway[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

export default function SelectGateway({
  value,
  onChange,
  gateways,
  placeholder = 'Select Gateway',
  disabled = false,
  className = '',
  error = false
}: SelectGatewayProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Use String comparison to handle both numeric IDs and Sqid string IDs
  const strValue = value != null && value !== 0 && value !== '' ? String(value) : '';
  const selectedGateway = strValue ? gateways.find(g => String(g.id) === strValue) : null;

  // Filter gateways based on search
  const filteredGateways = gateways.filter(gateway =>
    gateway.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (gateway.description && gateway.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  /**
   * Use onMouseDown (not onClick) to fire selection before the document-level
   * mousedown outside-click handler can close the dropdown.
   * Pass gateway.id as-is to preserve Sqid string IDs.
   */
  const handleOptionMouseDown = (e: React.MouseEvent, gatewayId: string | number) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(gatewayId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const isSelected = (gatewayId: string | number) =>
    strValue !== '' && String(gatewayId) === strValue;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(prev => !prev)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-md
          bg-background text-foreground
          ${error ? 'border-red-500' : 'border-input'}
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between
          transition-colors
        `}
      >
        <span className={selectedGateway ? '' : 'text-muted-foreground'}>
          {selectedGateway ? selectedGateway.name : placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute z-[9999] w-full mt-1 bg-popover border border-border rounded-md shadow-lg"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search gateways..."
              className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {filteredGateways.length > 0 ? (
              filteredGateways.map((gateway) => (
                <button
                  key={String(gateway.id)}
                  type="button"
                  onMouseDown={(e) => handleOptionMouseDown(e, gateway.id)}
                  className={`
                    w-full px-3 py-2 text-left rounded-md
                    hover:bg-accent hover:text-accent-foreground
                    flex items-center justify-between
                    transition-colors cursor-pointer
                    ${isSelected(gateway.id) ? 'bg-accent text-accent-foreground' : ''}
                  `}
                >
                  <div className="flex-1">
                    <div className="font-medium">{gateway.name}</div>
                    {gateway.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {gateway.description}
                      </div>
                    )}
                  </div>
                  {isSelected(gateway.id) && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No gateways found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
